// src/utils/redditAuth.ts
import pkceChallenge from 'pkce-challenge';
import { clearCache } from '../api/cache'; // Import clearCache

// --- Constants ---
export const CLIENT_ID_STORAGE_KEY = 'reddit_client_id'; // localStorage key for Client ID
export const AUTH_STATE_KEY = 'reddit_auth_state';       // localStorage key for CSRF state
export const CODE_VERIFIER_KEY = 'reddit_code_verifier'; // localStorage key for PKCE code_verifier
export const AUTH_TOKEN_KEY = 'reddit_auth_token';       // localStorage key for access token
export const REFRESH_TOKEN_KEY = 'reddit_refresh_token'; // localStorage key for refresh token
export const TOKEN_EXPIRY_KEY = 'reddit_token_expiry';   // localStorage key for token expiry timestamp

const REDDIT_TOKEN_URL = 'https://www.reddit.com/api/v1/access_token';
const REDDIT_AUTHORIZE_URL = 'https://www.reddit.com/api/v1/authorize';

// Use the Vercel system environment variable exposed via Vite's define config
// Fallback to a placeholder if the variable isn't set (e.g., during local dev without Vercel env)
const VERCEL_URL = import.meta.env.VITE_VERCEL_PROJECT_PRODUCTION_URL || null;

// Construct the production URL, ensuring it starts with https://
const PRODUCTION_URL = VERCEL_URL ? `https://${VERCEL_URL}` : 'https://your-app-name.vercel.app'; // Fallback placeholder

export const REDIRECT_URI = import.meta.env.DEV
    ? 'http://localhost:5173/callback' // Development URI
    : `${PRODUCTION_URL}/callback`;     // Production URI

// Permissions your app requests from the user. Added 'identity' for user info.
export const SCOPES = ['identity', 'read', 'mysubreddits'].join(',');

// --- Interfaces ---
interface TokenResponse {
    access_token: string;
    token_type: string; // Should be "bearer"
    expires_in: number; // Duration in seconds (e.g., 3600 for 1 hour)
    scope: string;      // Scopes granted
    refresh_token?: string; // Only present if duration=permanent was requested
}

// --- Helper Functions ---

/**
 * Generates a cryptographically secure random string.
 * @param {number} length - The desired length of the string.
 * @returns {string} A random alphanumeric string.
 */
const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);
    for (let i = 0; i < length; i++) {
        result += chars.charAt(randomValues[i]! % chars.length);
    }
    return result;
};

/**
 * Generates the state parameter for CSRF protection.
 * @param {number} length - The desired length of the state string.
 * @returns {string} A random alphanumeric string.
 */
export const generateState = (length = 16): string => {
    return generateRandomString(length);
};

/**
 * Generates the PKCE code verifier and challenge.
 * @returns {Promise<{code_verifier: string, code_challenge: string}>}
 */
export const generatePkce = async (): Promise<{ code_verifier: string, code_challenge: string }> => {
    return pkceChallenge();
};

/**
 * Constructs the Reddit Authorization URL for the Authorization Code Flow with PKCE.
 * @param {string} clientId - Your Reddit application's Client ID.
 * @param {string} state - The CSRF token generated by `generateState`.
 * @param {string} codeChallenge - The PKCE code challenge.
 * @returns {string} The full URL to redirect the user to for authorization.
 */
export const getAuthorizationUrl = (clientId: string, state: string, codeChallenge: string): string => {
    if (!clientId) {
        throw new Error("Client ID is required to generate authorization URL.");
    }
    if (PRODUCTION_URL === 'https://your-app-name.vercel.app' && !import.meta.env.DEV) {
        console.warn("Production URL seems to be using the placeholder. Ensure VERCEL_PROJECT_PRODUCTION_URL is available or update fallback.");
    }
    console.log(`Using Redirect URI for Auth: ${REDIRECT_URI}`);

    const params = new URLSearchParams({
        client_id: clientId,
        response_type: 'code', // Request an authorization code
        state: state,
        redirect_uri: REDIRECT_URI,
        duration: 'permanent', // Request a refresh token
        scope: SCOPES,
        code_challenge_method: 'S256', // PKCE method
        code_challenge: codeChallenge, // PKCE challenge
    });
    return `${REDDIT_AUTHORIZE_URL}?${params.toString()}`;
};

/**
 * Exchanges the authorization code for an access token and refresh token.
 * @param {string} code - The authorization code received from Reddit.
 * @param {string} codeVerifier - The PKCE code verifier stored earlier.
 * @returns {Promise<TokenResponse>} A promise resolving with the token data.
 */
export const exchangeCodeForToken = async (code: string, codeVerifier: string): Promise<TokenResponse> => {
    const clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (!clientId) {
        throw new Error("Client ID not found in storage for token exchange.");
    }

    const params = new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: REDIRECT_URI,
        code_verifier: codeVerifier, // Send the verifier
    });

    // Basic Authentication: Base64 encode "client_id:" (no client secret for public apps)
    const authHeader = `Basic ${btoa(`${clientId}:`)}`;

    try {
        const response = await fetch(REDDIT_TOKEN_URL, {
            method: 'POST',
            headers: {
                'Authorization': authHeader,
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params.toString(),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error("Token exchange failed:", data);
            throw new Error(data.error_description || data.error || `HTTP error! status: ${response.status}`);
        }

        return data as TokenResponse;
    } catch (error) {
        console.error("Error during token exchange:", error);
        throw error; // Re-throw the error after logging
    }
};

/**
 * Uses the refresh token to obtain a new access token.
 * @returns {Promise<TokenResponse>} A promise resolving with the new token data.
 */
export const refreshToken = async (): Promise<TokenResponse> => {
    const currentRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);

    if (!currentRefreshToken) {
        throw new Error("No refresh token available.");
    }
    if (!clientId) {
        throw new Error("Client ID not found in storage for token refresh.");
    }

    const params = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: currentRefreshToken,
    });

    // Basic Authentication: Base64 encode "client_id:"
    const authHeader = `Basic ${btoa(`${clientId}:`)}`;

    try {
        console.log("Attempting token refresh...");
        const response = await fetch(REDDIT_TOKEN_URL, {
            method: 'POST',
            headers: {
                'Authorization': authHeader,
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params.toString(),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error("Token refresh failed:", data);
            // If refresh fails (e.g., token revoked), clear auth data
            clearAuthData();
            throw new Error(data.error_description || data.error || `Refresh token failed: ${response.status}`);
        }

        console.log("Token refresh successful.");
        // Note: Reddit might return a new refresh token sometimes, but often doesn't.
        // We will only update the access token and expiry based on the response.
        // If a new refresh token *is* returned, we should store it.
        if (data.refresh_token) {
             console.log("Received new refresh token during refresh.");
             localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
        }
        // Store the new access token and calculate its expiry
        storeTokenData(data.access_token, data.expires_in, localStorage.getItem(REFRESH_TOKEN_KEY)); // Pass existing refresh token if not updated
        return data as TokenResponse;

    } catch (error) {
        console.error("Error during token refresh:", error);
        // Ensure auth data is cleared on any refresh error
        clearAuthData();
        throw error;
    }
};


/**
 * Stores the access token, expiry time, and optionally refresh token in Local Storage.
 * @param accessToken The access token string.
 * @param expiresIn Duration in seconds until the access token expires.
 * @param refreshToken Optional refresh token string.
 */
export const storeTokenData = (accessToken: string, expiresIn: number, refreshToken?: string | null): void => {
    const expiryTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    console.log(`Token data stored. Access token expires around: ${new Date(expiryTime).toLocaleTimeString()}`);
};


/**
 * Checks if the access token exists and is not expired.
 * Does NOT attempt to refresh the token here.
 * @returns {boolean} True if access token is present and valid, false otherwise.
 */
export const isAccessTokenValid = (): boolean => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !expiryString) {
        return false;
    }

    const expiryTime = parseInt(expiryString, 10);
    if (isNaN(expiryTime)) {
        clearAuthData(); // Clear invalid data
        return false;
    }
    // Add a small buffer (e.g., 60 seconds) to consider token expired slightly early
    const bufferSeconds = 60;
    return Date.now() < (expiryTime - bufferSeconds * 1000);
};

/**
 * Checks if the user is potentially authenticated (has a refresh token or a valid access token).
 * This is a preliminary check; API calls might still fail or require refresh.
 * @returns {boolean} True if a refresh token or valid access token exists.
 */
export const isAuthenticated = (): boolean => {
    return isAccessTokenValid() || !!localStorage.getItem(REFRESH_TOKEN_KEY);
};


/**
 * Removes all authentication-related data (tokens, state, verifier) from Local Storage
 * and clears the API cache.
 */
export const clearAuthData = (): void => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_STATE_KEY);
    localStorage.removeItem(CODE_VERIFIER_KEY);
    clearCache(); // Clear the API cache
    console.log("Authentication data (localStorage) and API cache cleared.");
};

// --- Token Refresh Logic for API Client ---
let isRefreshing = false;
let refreshPromise: Promise<TokenResponse | null> | null = null;

/**
 * Gets a valid access token, refreshing if necessary.
 * Handles concurrent refresh attempts.
 * @returns {Promise<string | null>} A valid access token or null if refresh fails.
 */
export const getValidAccessToken = async (): Promise<string | null> => {
    if (isAccessTokenValid()) {
        return localStorage.getItem(AUTH_TOKEN_KEY);
    }

    // If token is invalid/expired, check for refresh token
    const currentRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!currentRefreshToken) {
        console.log("Access token expired and no refresh token found.");
        clearAuthData(); // Clean up potentially expired access token etc.
        return null; // Cannot refresh
    }

    // Handle concurrent refresh requests
    if (isRefreshing && refreshPromise) {
        console.log("Token refresh already in progress, waiting...");
        const refreshedData = await refreshPromise;
        return refreshedData ? refreshedData.access_token : null;
    }

    // Initiate refresh
    isRefreshing = true;
    refreshPromise = refreshToken()
        .then(data => {
            isRefreshing = false;
            refreshPromise = null;
            return data;
        })
        .catch(error => {
            console.error("getValidAccessToken: Refresh failed", error);
            isRefreshing = false;
            refreshPromise = null;
            // clearAuthData() is called within refreshToken on failure
            return null; // Indicate failure
        });

    const refreshedData = await refreshPromise;
    return refreshedData ? refreshedData.access_token : null;
};